import React, { useState, useEffect } from "react";
import {
  BookOpen,
  Search,
  Plus,
  QrCode,
  User,
  Calendar,
  Download,
  Edit,
  Trash2,
  Camera,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./LibraryManagement.css";

const LibraryManagement = () => {
  const [activeTab, setActiveTab] = useState("catalog");
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddBook, setShowAddBook] = useState(false);
  const [showEditBook, setShowEditBook] = useState(false);
  const [showBorrowModal, setShowBorrowModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [books, setBooks] = useState([]);
  const [loans, setLoans] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [filterStatus, setFilterStatus] = useState("all");
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [generatedBarcode, setGeneratedBarcode] = useState("");

  // Initialize with mock data
  useEffect(() => {
    const initialBooks = [
      {
        id: 1,
        isbn: "978-0-123456-78-9",
        title: "Advanced Mathematics",
        author: "Dr. Sarah Johnson",
        category: "Mathematics",
        grade: "Grade 10-12",
        copies: 5,
        available: 3,
        barcode: "LIB001",
        publishedYear: 2023,
        status: "available",
        description: "Comprehensive mathematics textbook for advanced students",
        publisher: "Academic Press",
        language: "English",
        pages: 450,
        price: 89.99,
        location: "Shelf A-1",
        dateAdded: "2023-01-15",
      },
      {
        id: 2,
        isbn: "978-0-987654-32-1",
        title: "Physics Fundamentals",
        author: "Prof. Michael Chen",
        category: "Science",
        grade: "Grade 9-11",
        copies: 8,
        available: 6,
        barcode: "LIB002",
        publishedYear: 2022,
        status: "available",
        description: "Introduction to physics concepts and principles",
        publisher: "Science Publications",
        language: "English",
        pages: 320,
        price: 75.5,
        location: "Shelf B-2",
        dateAdded: "2022-11-20",
      },
      {
        id: 3,
        isbn: "978-0-555555-55-5",
        title: "Chemistry Lab Manual",
        author: "Dr. Emily Rodriguez",
        category: "Science",
        grade: "Grade 10-12",
        copies: 3,
        available: 0,
        barcode: "LIB003",
        publishedYear: 2023,
        status: "unavailable",
        description:
          "Laboratory exercises and experiments for chemistry students",
        publisher: "Lab Books Inc",
        language: "English",
        pages: 180,
        price: 45.0,
        location: "Shelf C-3",
        dateAdded: "2023-02-10",
      },
    ];

    const initialLoans = [
      {
        id: 1,
        bookId: 1,
        book: "Advanced Mathematics",
        student: "Emma Wilson",
        studentId: "STU001",
        borrowedDate: "2024-01-15",
        dueDate: "2024-01-29",
        returnDate: null,
        status: "active",
        daysOverdue: 0,
        fine: 0,
        renewed: 0,
      },
      {
        id: 2,
        bookId: 2,
        book: "Physics Fundamentals",
        student: "James Smith",
        studentId: "STU002",
        borrowedDate: "2024-01-10",
        dueDate: "2024-01-24",
        returnDate: null,
        status: "overdue",
        daysOverdue: 3,
        fine: 15.0,
        renewed: 1,
      },
      {
        id: 3,
        bookId: 3,
        book: "Chemistry Lab Manual",
        student: "Sarah Davis",
        studentId: "STU003",
        borrowedDate: "2024-01-05",
        dueDate: "2024-01-19",
        returnDate: "2024-01-18",
        status: "returned",
        daysOverdue: 0,
        fine: 0,
        renewed: 0,
      },
    ];

    setBooks(initialBooks);
    setLoans(initialLoans);
  }, []);

  // Utility functions
  const generateId = () =>
    Math.max(...books.map((b) => b.id), ...loans.map((l) => l.id), 0) + 1;
  const generateBarcode = () => `LIB${String(generateId()).padStart(3, "0")}`;

  const addNotification = (message, type = "success") => {
    const id = Date.now();
    setNotifications((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 5000);
  };

  const validateForm = (data, isEdit = false) => {
    const newErrors = {};

    if (!data.isbn?.trim()) newErrors.isbn = "ISBN is required";
    if (!data.title?.trim()) newErrors.title = "Title is required";
    if (!data.author?.trim()) newErrors.author = "Author is required";
    if (!data.category?.trim()) newErrors.category = "Category is required";
    if (!data.grade?.trim()) newErrors.grade = "Grade level is required";
    if (!data.copies || data.copies < 1)
      newErrors.copies = "At least 1 copy is required";
    if (
      !data.publishedYear ||
      data.publishedYear < 1900 ||
      data.publishedYear > new Date().getFullYear()
    ) {
      newErrors.publishedYear = "Valid publication year is required";
    }

    // Check for duplicate ISBN (only for new books)
    if (!isEdit && books.some((book) => book.isbn === data.isbn)) {
      newErrors.isbn = "ISBN already exists";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateDueDate = (borrowingPeriod, borrowingUnit) => {
    const today = new Date();
    const dueDate = new Date(today);
    const period = parseInt(borrowingPeriod) || 2;
    const unit = borrowingUnit || "weeks";

    switch (unit) {
      case "days":
        dueDate.setDate(today.getDate() + period);
        break;
      case "weeks":
        dueDate.setDate(today.getDate() + period * 7);
        break;
      case "months":
        dueDate.setMonth(today.getMonth() + period);
        break;
      default:
        dueDate.setDate(today.getDate() + period * 7);
    }

    return dueDate.toISOString().split("T")[0];
  };

  const calculateOverdueDays = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  const calculateFine = (daysOverdue) => {
    return daysOverdue * 5; // $5 per day
  };

  const tabs = [
    { id: "catalog", label: "Book Catalog", icon: BookOpen },
    { id: "loans", label: "Active Loans", icon: User },
    { id: "overdue", label: "Overdue Books", icon: Calendar },
    { id: "reports", label: "Reports", icon: Download },
  ];

  // Book Management Functions
  const handleAddBook = () => {
    if (!validateForm(formData)) return;

    const newBook = {
      id: generateId(),
      ...formData,
      barcode: generateBarcode(),
      available: parseInt(formData.copies),
      status: parseInt(formData.copies) > 0 ? "available" : "unavailable",
      dateAdded: new Date().toISOString().split("T")[0],
    };

    setBooks((prev) => [...prev, newBook]);
    setFormData({});
    setShowAddBook(false);
    addNotification(`Book "${newBook.title}" added successfully!`);
  };

  const handleEditBook = (book) => {
    setSelectedBook(book);
    setFormData({
      isbn: book.isbn,
      title: book.title,
      author: book.author,
      category: book.category,
      grade: book.grade,
      copies: book.copies,
      publishedYear: book.publishedYear,
      description: book.description || "",
      publisher: book.publisher || "",
      language: book.language || "English",
      pages: book.pages || "",
      price: book.price || "",
      location: book.location || "",
    });
    setShowEditBook(true);
  };

  const handleUpdateBook = () => {
    if (!validateForm(formData, true)) return;

    const updatedBook = {
      ...selectedBook,
      ...formData,
      available: Math.max(
        0,
        parseInt(formData.copies) -
          (selectedBook.copies - selectedBook.available)
      ),
      status:
        Math.max(
          0,
          parseInt(formData.copies) -
            (selectedBook.copies - selectedBook.available)
        ) > 0
          ? "available"
          : "unavailable",
    };

    setBooks((prev) =>
      prev.map((book) => (book.id === selectedBook.id ? updatedBook : book))
    );
    setFormData({});
    setSelectedBook(null);
    setShowEditBook(false);
    addNotification(`Book "${updatedBook.title}" updated successfully!`);
  };

  const handleDeleteBook = (book) => {
    if (
      window.confirm(
        `Are you sure you want to delete "${book.title}"?\n\nThis action cannot be undone.`
      )
    ) {
      // Check if book has active loans
      const activeLoans = loans.filter(
        (loan) =>
          loan.bookId === book.id &&
          (loan.status === "active" || loan.status === "overdue")
      );

      if (activeLoans.length > 0) {
        addNotification(
          `Cannot delete book. ${activeLoans.length} active loan(s) exist.`,
          "error"
        );
        return;
      }

      setBooks((prev) => prev.filter((b) => b.id !== book.id));
      addNotification(`Book "${book.title}" deleted successfully!`);
    }
  };

  const handleGenerateBarcode = (book) => {
    setSelectedBook(book);
    setGeneratedBarcode(book.barcode);
    setShowBarcodeModal(true);
  };

  // Loan Management Functions
  const handleBorrowBook = (book) => {
    setSelectedBook(book);
    setFormData({ borrowingPeriod: "2", borrowingUnit: "weeks" });
    setShowBorrowModal(true);
  };

  const handleProcessLoan = () => {
    const newErrors = {};

    if (!formData.studentId?.trim()) {
      newErrors.studentId = "Student ID is required";
    }

    if (!formData.borrowingPeriod || formData.borrowingPeriod < 1) {
      newErrors.borrowingPeriod = "Borrowing period must be at least 1";
    }

    if (!formData.borrowingUnit) {
      newErrors.borrowingUnit = "Please select a time unit";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newLoan = {
      id: generateId(),
      bookId: selectedBook.id,
      book: selectedBook.title,
      student: formData.studentName || `Student ${formData.studentId}`,
      studentId: formData.studentId,
      borrowedDate: new Date().toISOString().split("T")[0],
      dueDate: calculateDueDate(
        formData.borrowingPeriod,
        formData.borrowingUnit
      ),
      returnDate: null,
      status: "active",
      daysOverdue: 0,
      fine: 0,
      renewed: 0,
    };

    setLoans((prev) => [...prev, newLoan]);
    setBooks((prev) =>
      prev.map((book) =>
        book.id === selectedBook.id
          ? {
              ...book,
              available: book.available - 1,
              status: book.available - 1 > 0 ? "available" : "unavailable",
            }
          : book
      )
    );

    setFormData({});
    setShowBorrowModal(false);
    addNotification(
      `Book "${selectedBook.title}" loaned to ${newLoan.student} successfully!`
    );
  };

  const handleReturnBook = (loanId) => {
    const loan = loans.find((l) => l.id === loanId);
    if (!loan) return;

    const returnDate = new Date().toISOString().split("T")[0];
    const daysOverdue = calculateOverdueDays(loan.dueDate);
    const fine = calculateFine(daysOverdue);

    setLoans((prev) =>
      prev.map((l) =>
        l.id === loanId
          ? { ...l, returnDate, status: "returned", daysOverdue, fine }
          : l
      )
    );

    setBooks((prev) =>
      prev.map((book) =>
        book.id === loan.bookId
          ? { ...book, available: book.available + 1, status: "available" }
          : book
      )
    );

    addNotification(
      `Book "${loan.book}" returned successfully!${
        fine > 0 ? ` Fine: $${fine}` : ""
      }`
    );
  };

  // Filter and Export Functions
  const handleFilterLoans = (status) => {
    setFilterStatus(status);
  };

  const handleExportLoans = () => {
    const activeLoans = loans.filter(
      (loan) => loan.status === "active" || loan.status === "overdue"
    );

    if (activeLoans.length === 0) {
      addNotification("No active loans to export", "error");
      return;
    }

    const csvContent = [
      [
        "Book",
        "Student",
        "Student ID",
        "Borrowed Date",
        "Due Date",
        "Status",
        "Days Overdue",
        "Fine",
      ].join(","),
      ...activeLoans.map((loan) =>
        [
          `"${loan.book}"`,
          `"${loan.student}"`,
          loan.studentId,
          loan.borrowedDate,
          loan.dueDate,
          loan.status,
          loan.daysOverdue,
          loan.fine,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `library_loans_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    addNotification("Loans exported successfully!");
  };

  const handleSendReminder = (loan) => {
    // In a real app, this would send an email/SMS
    addNotification(`Reminder sent to ${loan.student} for "${loan.book}"`);
  };

  const handleMarkLost = (loan) => {
    if (
      window.confirm(
        `Mark "${loan.book}" as lost for ${loan.student}?\n\nThis will charge the replacement cost.`
      )
    ) {
      const replacementCost = 50; // Default replacement cost

      setLoans((prev) =>
        prev.map((l) =>
          l.id === loan.id ? { ...l, status: "lost", fine: replacementCost } : l
        )
      );

      setBooks((prev) =>
        prev.map((book) =>
          book.id === loan.bookId
            ? {
                ...book,
                copies: book.copies - 1,
                available: Math.max(0, book.available - 1),
              }
            : book
        )
      );

      addNotification(
        `Book "${loan.book}" marked as lost. Replacement cost: $${replacementCost}`
      );
    }
  };

  const handleRenewLoan = (loanId) => {
    const loan = loans.find((l) => l.id === loanId);
    if (!loan) return;

    if (loan.renewed >= 2) {
      addNotification("Maximum renewals reached for this book", "error");
      return;
    }

    const newDueDate = calculateDueDate("2", "weeks");

    setLoans((prev) =>
      prev.map((l) =>
        l.id === loanId
          ? { ...l, dueDate: newDueDate, renewed: l.renewed + 1 }
          : l
      )
    );

    addNotification(
      `Loan renewed for "${loan.book}". New due date: ${newDueDate}`
    );
  };

  // Report Generation
  const handleGenerateReport = () => {
    const reportData = {
      totalBooks: books.length,
      totalCopies: books.reduce((sum, book) => sum + book.copies, 0),
      availableBooks: books.reduce((sum, book) => sum + book.available, 0),
      totalLoans: loans.length,
      activeLoans: loans.filter((loan) => loan.status === "active").length,
      overdueLoans: loans.filter((loan) => loan.status === "overdue").length,
      totalFines: loans.reduce((sum, loan) => sum + loan.fine, 0),
      mostBorrowedBooks: getMostBorrowedBooks(),
      categoryStats: getCategoryStats(),
    };

    const reportContent = generateReportContent(reportData);

    const blob = new Blob([reportContent], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `library_report_${new Date().toISOString().split("T")[0]}.txt`;
    a.click();
    window.URL.revokeObjectURL(url);

    addNotification("Library report generated successfully!");
  };

  const getMostBorrowedBooks = () => {
    const bookLoanCounts = {};
    loans.forEach((loan) => {
      bookLoanCounts[loan.book] = (bookLoanCounts[loan.book] || 0) + 1;
    });

    return Object.entries(bookLoanCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([book, count]) => ({ book, count }));
  };

  const getCategoryStats = () => {
    const categoryCounts = {};
    books.forEach((book) => {
      categoryCounts[book.category] = (categoryCounts[book.category] || 0) + 1;
    });

    return Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
    }));
  };

  const generateReportContent = (data) => {
    return `LIBRARY MANAGEMENT SYSTEM REPORT
Generated on: ${new Date().toLocaleDateString()}

=== OVERVIEW ===
Total Books: ${data.totalBooks}
Total Copies: ${data.totalCopies}
Available Books: ${data.availableBooks}
Total Loans: ${data.totalLoans}
Active Loans: ${data.activeLoans}
Overdue Loans: ${data.overdueLoans}
Total Fines: $${data.totalFines.toFixed(2)}

=== MOST BORROWED BOOKS ===
${data.mostBorrowedBooks
  .map((item, index) => `${index + 1}. ${item.book}: ${item.count} loans`)
  .join("\n")}

=== CATEGORY STATISTICS ===
${data.categoryStats
  .map((item) => `${item.category}: ${item.count} books`)
  .join("\n")}

=== END OF REPORT ===`;
  };

  // Filtered data
  const filteredBooks = books.filter(
    (book) =>
      book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.isbn.includes(searchTerm) ||
      book.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredLoans = loans.filter((loan) => {
    if (filterStatus === "all")
      return loan.status === "active" || loan.status === "overdue";
    return loan.status === filterStatus;
  });

  const overdueLoans = loans.filter((loan) => loan.status === "overdue");

  // Form handling
  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const resetForm = () => {
    setFormData({});
    setErrors({});
  };

  const renderBookCatalog = () => (
    <div className="catalog-section">
      <div className="section-header">
        <div className="search-bar">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search books by title, author, or ISBN..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowAddBook(true)}
        >
          <Plus size={20} />
          Add Book
        </button>
      </div>

      <div className="books-grid">
        {filteredBooks.map((book) => (
          <div key={book.id} className="book-card">
            <div className="book-header">
              <div className="book-status">
                <span className={`status-badge status-${book.status}`}>
                  {book.status === "available" ? "Available" : "Unavailable"}
                </span>
              </div>
              <div className="book-actions">
                <button
                  className="action-btn"
                  onClick={() => handleGenerateBarcode(book)}
                >
                  <QrCode size={16} />
                </button>
                <button
                  className="action-btn"
                  onClick={() => handleEditBook(book)}
                >
                  <Edit size={16} />
                </button>
                <button
                  className="action-btn"
                  onClick={() => handleDeleteBook(book)}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="book-info">
              <h3 className="book-title">{book.title}</h3>
              <p className="book-author">by {book.author}</p>
              <div className="book-details">
                <span className="detail-item">
                  <strong>ISBN:</strong> {book.isbn}
                </span>
                <span className="detail-item">
                  <strong>Category:</strong> {book.category}
                </span>
                <span className="detail-item">
                  <strong>Grade:</strong> {book.grade}
                </span>
                <span className="detail-item">
                  <strong>Year:</strong> {book.publishedYear}
                </span>
              </div>
            </div>

            <div className="book-footer">
              <div className="availability">
                <span className="available-count">{book.available}</span>
                <span className="total-count">/{book.copies} copies</span>
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => handleBorrowBook(book)}
                disabled={book.available === 0}
              >
                Borrow
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderActiveLoans = () => (
    <div className="loans-section">
      <div className="section-header">
        <h3>Active Loans</h3>
        <div className="filter-actions">
          <select
            value={filterStatus}
            onChange={(e) => handleFilterLoans(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Active</option>
            <option value="active">Active Only</option>
            <option value="overdue">Overdue Only</option>
          </select>
          <button className="btn btn-outline" onClick={handleExportLoans}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="loans-table">
        <table>
          <thead>
            <tr>
              <th>Book</th>
              <th>Student</th>
              <th>Borrowed Date</th>
              <th>Due Date</th>
              <th>Status</th>
              <th>Fine</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredLoans.map((loan) => (
              <tr key={loan.id}>
                <td>
                  <div className="book-info-cell">
                    <strong>{loan.book}</strong>
                    <span className="student-id">ID: {loan.studentId}</span>
                  </div>
                </td>
                <td>{loan.student}</td>
                <td>{loan.borrowedDate}</td>
                <td>
                  <span
                    className={loan.status === "overdue" ? "overdue-date" : ""}
                  >
                    {loan.dueDate}
                  </span>
                </td>
                <td>
                  <span className={`status-badge status-${loan.status}`}>
                    {loan.status === "overdue"
                      ? `Overdue (${loan.daysOverdue} days)`
                      : "Active"}
                  </span>
                </td>
                <td>{loan.fine > 0 ? `$${loan.fine.toFixed(2)}` : "-"}</td>
                <td>
                  <div className="loan-actions">
                    {loan.status === "active" && loan.renewed < 2 && (
                      <button
                        className="btn btn-warning btn-sm"
                        onClick={() => handleRenewLoan(loan.id)}
                        title="Renew Loan"
                      >
                        Renew
                      </button>
                    )}
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleReturnBook(loan.id)}
                    >
                      Return
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredLoans.length === 0 && (
          <div className="no-data">
            <p>No active loans found.</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderOverdueBooks = () => {
    const totalFines = overdueLoans.reduce((sum, loan) => sum + loan.fine, 0);

    return (
      <div className="overdue-section">
        <div className="section-header">
          <h3>Overdue Books</h3>
          <div className="overdue-stats">
            <div className="stat-item">
              <span className="stat-number">{overdueLoans.length}</span>
              <span className="stat-label">Total Overdue</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">${totalFines.toFixed(2)}</span>
              <span className="stat-label">Total Fines</span>
            </div>
          </div>
        </div>

        <div className="overdue-list">
          {overdueLoans.map((loan) => (
            <div key={loan.id} className="overdue-item">
              <div className="overdue-info">
                <h4>{loan.book}</h4>
                <p>
                  Borrowed by: {loan.student} ({loan.studentId})
                </p>
                <p>
                  Due: {loan.dueDate} • {loan.daysOverdue} days overdue
                </p>
                <p className="fine-amount">Fine: ${loan.fine.toFixed(2)}</p>
              </div>
              <div className="overdue-actions">
                <button
                  className="btn btn-warning btn-sm"
                  onClick={() => handleSendReminder(loan)}
                >
                  Send Reminder
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleReturnBook(loan.id)}
                >
                  Return
                </button>
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleMarkLost(loan)}
                >
                  Mark Lost
                </button>
              </div>
            </div>
          ))}
          {overdueLoans.length === 0 && (
            <div className="no-data">
              <p>No overdue books found. Great job!</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderReports = () => {
    const mostBorrowed = getMostBorrowedBooks();
    const categoryStats = getCategoryStats();
    const totalFines = loans.reduce((sum, loan) => sum + loan.fine, 0);

    return (
      <div className="reports-section">
        <div className="section-header">
          <h3>Library Reports</h3>
          <button className="btn btn-primary" onClick={handleGenerateReport}>
            <Download size={16} />
            Generate Report
          </button>
        </div>

        <div className="reports-grid">
          <div className="report-card">
            <h4>Library Overview</h4>
            <div className="report-content">
              <div className="report-item">
                <span>Total Books</span>
                <span>{books.length}</span>
              </div>
              <div className="report-item">
                <span>Total Copies</span>
                <span>{books.reduce((sum, book) => sum + book.copies, 0)}</span>
              </div>
              <div className="report-item">
                <span>Available Books</span>
                <span>
                  {books.reduce((sum, book) => sum + book.available, 0)}
                </span>
              </div>
              <div className="report-item">
                <span>Total Loans</span>
                <span>{loans.length}</span>
              </div>
              <div className="report-item">
                <span>Active Loans</span>
                <span>
                  {loans.filter((loan) => loan.status === "active").length}
                </span>
              </div>
              <div className="report-item">
                <span>Overdue Loans</span>
                <span>{overdueLoans.length}</span>
              </div>
              <div className="report-item">
                <span>Total Fines</span>
                <span>${totalFines.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="report-card">
            <h4>Most Borrowed Books</h4>
            <div className="report-content">
              {mostBorrowed.length > 0 ? (
                mostBorrowed.map((item, index) => (
                  <div key={index} className="report-item">
                    <span>{item.book}</span>
                    <span>{item.count} loans</span>
                  </div>
                ))
              ) : (
                <div className="no-data">
                  <p>No borrowing data available</p>
                </div>
              )}
            </div>
          </div>

          <div className="report-card">
            <h4>Category Statistics</h4>
            <div className="report-content">
              {categoryStats.map((item, index) => (
                <div key={index} className="report-item">
                  <span>{item.category}</span>
                  <span>{item.count} books</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="library-management">
      <div className="page-header">
        <h1>Library Management System</h1>
        <p>Manage books, loans, and library operations</p>
      </div>

      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={20} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="tab-content">
        {activeTab === "catalog" && renderBookCatalog()}
        {activeTab === "loans" && renderActiveLoans()}
        {activeTab === "overdue" && renderOverdueBooks()}
        {activeTab === "reports" && renderReports()}
      </div>

      {/* Add Book Modal */}
      {showAddBook && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New Book</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowAddBook(false);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-row">
                <div className="form-group">
                  <label>ISBN *</label>
                  <div className="isbn-input">
                    <input
                      type="text"
                      placeholder="Enter ISBN"
                      value={formData.isbn || ""}
                      onChange={(e) => handleFormChange("isbn", e.target.value)}
                      className={errors.isbn ? "error" : ""}
                    />
                    <button className="btn btn-outline" type="button">
                      <Camera size={16} />
                      Scan
                    </button>
                  </div>
                  {errors.isbn && (
                    <span className="error-message">{errors.isbn}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    placeholder="Book title"
                    value={formData.title || ""}
                    onChange={(e) => handleFormChange("title", e.target.value)}
                    className={errors.title ? "error" : ""}
                  />
                  {errors.title && (
                    <span className="error-message">{errors.title}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Author *</label>
                  <input
                    type="text"
                    placeholder="Author name"
                    value={formData.author || ""}
                    onChange={(e) => handleFormChange("author", e.target.value)}
                    className={errors.author ? "error" : ""}
                  />
                  {errors.author && (
                    <span className="error-message">{errors.author}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category || ""}
                    onChange={(e) =>
                      handleFormChange("category", e.target.value)
                    }
                    className={errors.category ? "error" : ""}
                  >
                    <option value="">Select Category</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Science">Science</option>
                    <option value="Literature">Literature</option>
                    <option value="History">History</option>
                    <option value="Art">Art</option>
                    <option value="Technology">Technology</option>
                    <option value="Reference">Reference</option>
                  </select>
                  {errors.category && (
                    <span className="error-message">{errors.category}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Grade Level *</label>
                  <select
                    value={formData.grade || ""}
                    onChange={(e) => handleFormChange("grade", e.target.value)}
                    className={errors.grade ? "error" : ""}
                  >
                    <option value="">Select Grade</option>
                    <option value="Grade 6-8">Grade 6-8</option>
                    <option value="Grade 9-12">Grade 9-12</option>
                    <option value="All Grades">All Grades</option>
                  </select>
                  {errors.grade && (
                    <span className="error-message">{errors.grade}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Number of Copies *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.copies || 1}
                    onChange={(e) =>
                      handleFormChange("copies", parseInt(e.target.value))
                    }
                    className={errors.copies ? "error" : ""}
                  />
                  {errors.copies && (
                    <span className="error-message">{errors.copies}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Published Year *</label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear()}
                    placeholder="2023"
                    value={formData.publishedYear || ""}
                    onChange={(e) =>
                      handleFormChange(
                        "publishedYear",
                        parseInt(e.target.value)
                      )
                    }
                    className={errors.publishedYear ? "error" : ""}
                  />
                  {errors.publishedYear && (
                    <span className="error-message">
                      {errors.publishedYear}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Publisher</label>
                  <input
                    type="text"
                    placeholder="Publisher name"
                    value={formData.publisher || ""}
                    onChange={(e) =>
                      handleFormChange("publisher", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Book description"
                  rows="3"
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowAddBook(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleAddBook}>
                Add Book
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Book Modal */}
      {showEditBook && selectedBook && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit Book</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowEditBook(false);
                  setSelectedBook(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="form-row">
                <div className="form-group">
                  <label>ISBN *</label>
                  <input
                    type="text"
                    value={formData.isbn || ""}
                    onChange={(e) => handleFormChange("isbn", e.target.value)}
                    className={errors.isbn ? "error" : ""}
                    disabled
                  />
                  {errors.isbn && (
                    <span className="error-message">{errors.isbn}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    value={formData.title || ""}
                    onChange={(e) => handleFormChange("title", e.target.value)}
                    className={errors.title ? "error" : ""}
                  />
                  {errors.title && (
                    <span className="error-message">{errors.title}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Author *</label>
                  <input
                    type="text"
                    value={formData.author || ""}
                    onChange={(e) => handleFormChange("author", e.target.value)}
                    className={errors.author ? "error" : ""}
                  />
                  {errors.author && (
                    <span className="error-message">{errors.author}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    value={formData.category || ""}
                    onChange={(e) =>
                      handleFormChange("category", e.target.value)
                    }
                    className={errors.category ? "error" : ""}
                  >
                    <option value="">Select Category</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Science">Science</option>
                    <option value="Literature">Literature</option>
                    <option value="History">History</option>
                    <option value="Art">Art</option>
                    <option value="Technology">Technology</option>
                    <option value="Reference">Reference</option>
                  </select>
                  {errors.category && (
                    <span className="error-message">{errors.category}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Grade Level *</label>
                  <select
                    value={formData.grade || ""}
                    onChange={(e) => handleFormChange("grade", e.target.value)}
                    className={errors.grade ? "error" : ""}
                  >
                    <option value="">Select Grade</option>
                    <option value="Grade 6-8">Grade 6-8</option>
                    <option value="Grade 9-12">Grade 9-12</option>
                    <option value="All Grades">All Grades</option>
                  </select>
                  {errors.grade && (
                    <span className="error-message">{errors.grade}</span>
                  )}
                </div>
                <div className="form-group">
                  <label>Number of Copies *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.copies || 1}
                    onChange={(e) =>
                      handleFormChange("copies", parseInt(e.target.value))
                    }
                    className={errors.copies ? "error" : ""}
                  />
                  {errors.copies && (
                    <span className="error-message">{errors.copies}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Published Year *</label>
                  <input
                    type="number"
                    min="1900"
                    max={new Date().getFullYear()}
                    value={formData.publishedYear || ""}
                    onChange={(e) =>
                      handleFormChange(
                        "publishedYear",
                        parseInt(e.target.value)
                      )
                    }
                    className={errors.publishedYear ? "error" : ""}
                  />
                  {errors.publishedYear && (
                    <span className="error-message">
                      {errors.publishedYear}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Publisher</label>
                  <input
                    type="text"
                    value={formData.publisher || ""}
                    onChange={(e) =>
                      handleFormChange("publisher", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  rows="3"
                  value={formData.description || ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowEditBook(false);
                  setSelectedBook(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleUpdateBook}>
                Update Book
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Borrow Book Modal */}
      {showBorrowModal && selectedBook && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Borrow Book</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowBorrowModal(false);
                  setSelectedBook(null);
                  resetForm();
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="book-summary">
                <h4>{selectedBook.title}</h4>
                <p>by {selectedBook.author}</p>
                <p>ISBN: {selectedBook.isbn}</p>
                <p>
                  Available: {selectedBook.available} of {selectedBook.copies}{" "}
                  copies
                </p>
              </div>
              <div className="form-group">
                <label>Student ID *</label>
                <input
                  type="text"
                  placeholder="Enter student ID"
                  value={formData.studentId || ""}
                  onChange={(e) =>
                    handleFormChange("studentId", e.target.value)
                  }
                  className={errors.studentId ? "error" : ""}
                />
                {errors.studentId && (
                  <span className="error-message">{errors.studentId}</span>
                )}
              </div>
              <div className="form-group">
                <label>Student Name</label>
                <input
                  type="text"
                  placeholder="Enter student name (optional)"
                  value={formData.studentName || ""}
                  onChange={(e) =>
                    handleFormChange("studentName", e.target.value)
                  }
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Borrowing Period *</label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    placeholder="2"
                    value={formData.borrowingPeriod || "2"}
                    onChange={(e) =>
                      handleFormChange("borrowingPeriod", e.target.value)
                    }
                    className={errors.borrowingPeriod ? "error" : ""}
                  />
                  {errors.borrowingPeriod && (
                    <span className="error-message">
                      {errors.borrowingPeriod}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Time Unit *</label>
                  <select
                    value={formData.borrowingUnit || "weeks"}
                    onChange={(e) =>
                      handleFormChange("borrowingUnit", e.target.value)
                    }
                    className={errors.borrowingUnit ? "error" : ""}
                  >
                    <option value="days">Days</option>
                    <option value="weeks">Weeks</option>
                    <option value="months">Months</option>
                  </select>
                  {errors.borrowingUnit && (
                    <span className="error-message">
                      {errors.borrowingUnit}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => {
                  setShowBorrowModal(false);
                  setSelectedBook(null);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleProcessLoan}>
                Process Loan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Modal */}
      {showBarcodeModal && selectedBook && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Book Barcode</h3>
              <button
                className="close-btn"
                onClick={() => {
                  setShowBarcodeModal(false);
                  setSelectedBook(null);
                  setGeneratedBarcode("");
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="barcode-display">
                <div className="book-info">
                  <h4>{selectedBook.title}</h4>
                  <p>by {selectedBook.author}</p>
                  <p>ISBN: {selectedBook.isbn}</p>
                </div>
                <div className="barcode-container">
                  <div className="barcode">{generatedBarcode}</div>
                  <p className="barcode-label">
                    Library Barcode: {generatedBarcode}
                  </p>
                </div>
                <div className="barcode-actions">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      // In a real app, this would print the barcode
                      addNotification("Barcode sent to printer");
                    }}
                  >
                    Print Barcode
                  </button>
                  <button
                    className="btn btn-outline"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedBarcode);
                      addNotification("Barcode copied to clipboard");
                    }}
                  >
                    Copy Barcode
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`notification notification-${notification.type}`}
          >
            <div className="notification-content">
              {notification.type === "success" ? (
                <CheckCircle size={20} />
              ) : (
                <AlertCircle size={20} />
              )}
              <span>{notification.message}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LibraryManagement;
